import React, { useState, useEffect } from 'react';
import api from '../api';
import GoogleSignInButton from './GoogleSignInButton';
import { googleAuthService, GoogleAuthResponse } from '../services/GoogleAuthService';

interface LoginFormProps {
  onLoginSuccess: (user: any, token: string) => void;
  onError?: (error: string) => void;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: string;
    is_active: boolean;
    created_at: string;
  };
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, onError }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleConfigured, setIsGoogleConfigured] = useState(false);

  // Check if Google OAuth is configured and handle OAuth callback on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Check if Google OAuth is configured
      setIsGoogleConfigured(googleAuthService.isGoogleOAuthConfigured());

      // Handle OAuth callback from URL hash (Google redirect)
      try {
        const hash = window.location.hash;
        if (hash.includes('google-callback')) {
          // Extract data from hash
          const params = new URLSearchParams(hash.split('?')[1]);
          const dataStr = params.get('data');
          
          if (dataStr) {
            const authData = JSON.parse(decodeURIComponent(dataStr));
            
            // Store authentication data
            localStorage.setItem('auth_token', authData.access_token);
            localStorage.setItem('user', JSON.stringify(authData.user));
            api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
            
            // Clean up URL
            window.location.hash = '';
            
            // Call success handler
            onLoginSuccess(authData.user, authData.access_token);
            return;
          }
        }
        
        // Fallback: Handle OAuth callback if present in URL query params
        const authResponse = await googleAuthService.handleCallbackFromUrl();
        if (authResponse) {
          onLoginSuccess(authResponse.user, authResponse.access_token);
        }
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        const errorMessage = error.message || 'Google authentication failed';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    };

    initializeAuth();
  }, [onLoginSuccess, onError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        username,
        password
      });

      const { access_token, user } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set default authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      onLoginSuccess(user, access_token);
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.status === 401 
        ? 'Invalid username or password'
        : err.response?.data?.detail || 'Login failed. Please try again.';
      
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (response: GoogleAuthResponse) => {
    console.log('Google authentication successful:', response.user.email);
    onLoginSuccess(response.user, response.access_token);
  };

  const handleGoogleError = (error: Error) => {
    console.error('Google authentication error:', error);
    const errorMessage = error.message || 'Google authentication failed';
    setError(errorMessage);
    setGoogleLoading(false);
    if (onError) {
      onError(errorMessage);
    }
  };

  return (
    <div className="login-container">
      {/* Animated floating merch items */}
      <div className="login-floating-merch">
        <div className="merch-item merch-1">👕</div>
        <div className="merch-item merch-2">🎒</div>
        <div className="merch-item merch-3">☕</div>
        <div className="merch-item merch-4">🧢</div>
        <div className="merch-item merch-5">📓</div>
        <div className="merch-item merch-6">🖊️</div>
        <div className="merch-item merch-7">🏷️</div>
        <div className="merch-item merch-8">🎁</div>
        <div className="merch-item merch-9">👜</div>
        <div className="merch-item merch-10">🧴</div>
        <div className="merch-item merch-11">⌚</div>
        <div className="merch-item merch-12">🎧</div>
      </div>

      <div className="login-form">
        <div className="login-header">
          <img src="/logogear_nexus_logo.svg" alt="Logogear Nexus" style={{ width: '180px', height: 'auto', marginBottom: '8px' }} />
        </div>

        {/* Google Authentication Section */}
        {isGoogleConfigured && (
          <div className="google-auth-section">
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={loading || googleLoading}
              className="full-width"
            />
          </div>
        )}

        {/* Divider between authentication methods */}
        {isGoogleConfigured && (
          <div className="auth-divider">
            <span>or continue with email</span>
          </div>
        )}

        {/* Traditional Authentication Section */}
        <div className="traditional-auth-section">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading || googleLoading}
                placeholder="Enter your username"
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading || googleLoading}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading || googleLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading || googleLoading || !username || !password}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Login footer with additional info */}
        <div className="login-footer">
          <p>
            {isGoogleConfigured 
              ? 'Choose your preferred sign-in method above'
              : 'Sign in with your username and password'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;